import { CheckCircle2 } from "lucide-react";

import { AttachmentBubble } from "./AttachmentBubble";

interface Props {
  outputPreview: string;
  /**
   * IDs de anexos gerados por tools como ``save_artifact`` /
   * ``build_report``. Quando presentes, renderizamos um balao de
   * arquivo clicavel acima do preview cru. Vem do
   * ``ExecutionStep.payload`` quando o backend identifica que o
   * tool_result carregou artefato(s). M3.
   */
  attachmentIds?: string[];
  /**
   * Quando o toggle "Mostrar uso de ferramentas" esta DESLIGADO mas o
   * tool_result carregou arquivo(s): renderiza SO os baloes de arquivo,
   * sem o detalhe cru da tool. Arquivos gerados (relatorios, graficos) sao
   * essenciais ao chat e nunca devem sumir junto com as tools.
   */
  filesOnly?: boolean;
}

/**
 * Extrai ids de anexo de um payload de tool_result. Aceita o formato
 * estruturado ({attachment_id}, {html_attachment_id, pdf_attachment_id})
 * ou os arrays diretos quando o backend ja extraiu. Funcao pura para
 * facilitar testes.
 */
export function extractAttachmentIdsFromPreview(preview: string): string[] {
  try {
    const parsed = JSON.parse(preview);
    if (!parsed || typeof parsed !== "object") return [];
    const ids: string[] = [];
    const obj = parsed as Record<string, unknown>;
    if (typeof obj.attachment_id === "string") ids.push(obj.attachment_id);
    if (typeof obj.html_attachment_id === "string")
      ids.push(obj.html_attachment_id);
    if (typeof obj.pdf_attachment_id === "string")
      ids.push(obj.pdf_attachment_id);
    return ids;
  } catch {
    return [];
  }
}

export function ToolResultBlock({
  outputPreview,
  attachmentIds,
  filesOnly,
}: Props) {
  const truncated = outputPreview.length > 200;
  const short = truncated ? outputPreview.slice(0, 200) + "..." : outputPreview;
  const ids =
    attachmentIds && attachmentIds.length > 0
      ? attachmentIds
      : extractAttachmentIdsFromPreview(outputPreview);

  // Tools ocultas, mas com arquivo(s): mostra apenas os baloes de arquivo.
  if (filesOnly) {
    if (ids.length === 0) return null;
    return (
      <div className="my-2 flex flex-wrap gap-2">
        {ids.map((id) => (
          <AttachmentBubble key={id} attachmentId={id} kind="output" />
        ))}
      </div>
    );
  }

  return (
    <div className="my-2 space-y-2">
      {ids.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {ids.map((id) => (
            <AttachmentBubble key={id} attachmentId={id} kind="output" />
          ))}
        </div>
      ) : null}
      <details className="bg-card rounded-md border p-2 text-xs">
        <summary className="flex cursor-pointer items-center gap-1 font-medium">
          <CheckCircle2 className="h-3 w-3 text-green-600" />
          <span>Resultado da tool</span>
        </summary>
        <pre className="bg-muted/30 mt-2 max-h-72 overflow-auto rounded p-2 font-mono whitespace-pre-wrap">
          {truncated ? outputPreview : short}
        </pre>
      </details>
    </div>
  );
}
